import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import AdornmentSelect from '@/components/Selects/AdornmentSelect';
import TextField from '@/components/TextField/TextField';
import ArrowDownIcon from '@/images/ArrowDown.svg';
import CalendarIcon from '@/images/CalendarMark.svg';
import { useGetAgents } from '@/pages/agents/api';
import { useAuth } from '@/services/auth';
import theme from '@/theme';
import { IAgent } from '@d-id/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, DialogActions, DialogContent, DialogTitle, Popover, Typography } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useAgentInsightsContext } from '../../contex/useAgentInsightsContext';
import { useExportChats } from '../../hooks/useExportChats';

const ALL_TIME_LABEL = 'All time';

interface Props {
    onClose: () => void;
}

const StyledDialogTitle = styled(DialogTitle)`
    padding: 2.4rem 2.4rem 1.6rem !important;
`;

const StyledDialogContent = styled(DialogContent)`
    padding: 0 2.4rem 0 !important;
`;

const StyledDialogActions = styled(DialogActions)`
    padding: 0 2.4rem 2.4rem !important;
    justify-content: flex-end !important;
`;

const IconButtonStyled = styled(IconButton)`
    position: absolute;
    top: 1.6rem;
    right: 1.6rem;
`;

const FormRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    margin-bottom: 1.6rem;
`;

const Label = styled(Typography)`
    font-size: 1.4rem;
    font-weight: 500;
    line-height: 2rem;
    color: ${theme.colors.secondary[900]};
`;

const Subtitle = styled(Typography)`
    font-size: 1.4rem;
    line-height: 2rem;
    color: ${theme.colors.secondary[700]};
    margin-bottom: 2.4rem;
`;

const StyledAdornmentSelect = styled(AdornmentSelect<IAgent | null>)`
    max-width: 150px;

    & .MuiInputBase-root {
        padding: 0.1rem 3.9rem 0.1rem 1.2rem;
        min-height: 4rem;
        height: 4rem;
    }

    & .MuiInputBase-input {
        font-size: 1.4rem;
        cursor: pointer;
        padding: 0.8rem 0;
    }

    & .MuiOutlinedInput-notchedOutline {
        border-radius: 0.8rem;
        border-color: ${theme.colors.black[300]};
    }
`;

const DateRangeButton = styled(Button)<{ hasValue: boolean }>`
    ${({ hasValue }) =>
        hasValue
            ? `
        width: fit-content;
        min-width: 15rem;
    `
            : `
        width: 15rem;
    `}
    padding: 0.8rem 1.2rem;
    border: 1px solid ${theme.colors.black[300]} !important;
    border-radius: 0.8rem;
    background-color: ${theme.colors.white} !important;
    color: ${theme.colors.secondary[900]};
    font-size: 1.4rem;
    font-weight: 400;
    text-align: left;
    justify-content: flex-start;
    text-transform: none;
    gap: 0.8rem;

    &:hover,
    &:focus,
    &:active {
        background-color: ${theme.colors.white} !important;
        border: 1px solid ${theme.colors.secondary.main} !important;
        border-color: ${theme.colors.secondary.main} !important;
        box-shadow: none !important;
    }

    &.MuiButtonBase-root {
        min-height: 4rem;
    }

    &.MuiButtonBase-root:hover {
        background-color: ${theme.colors.white} !important;
        border-color: ${theme.colors.secondary.main} !important;
    }

    & .MuiButton-endIcon {
        margin-left: auto;
        margin-right: 0;
    }

    & .MuiButton-startIcon {
        margin-left: 0;
        margin-right: 0;
    }
`;

const DateRangePopover = styled.div`
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 300px;
`;

const DateInputRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const DateLabel = styled(Typography)`
    font-size: 12px;
    font-weight: 600;
    color: ${theme.colors.secondary[900]};
`;

const DateRangeActions = styled.div`
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    padding-top: 8px;
`;

const ActionButton = styled(Button)`
    padding: 8px 16px;
    font-size: 14px;
    text-transform: none;
`;

const ExportButton = styled(Button)`
    height: 3.2rem;
    min-width: 8.8rem;
    padding: 0 1.2rem;
    font-size: 1.4rem;
    border-radius: 0.8rem;
`;

const AlertContainer = styled.div`
    width: 100%;
`;

export const ExportConversationsModal = ({ onClose }: Props) => {
    const { agent: contextAgent } = useAgentInsightsContext();
    const { exportChats, isExporting, error, clearError } = useExportChats();
    const { isAnonymous } = useAuth();
    const { data } = useGetAgents(isAnonymous);

    const agentList = useMemo(() => data?.items.filter(a => a.access !== 'public') || [], [data?.items]);

    const [selectedAgent, setSelectedAgent] = useState<IAgent | null>(() => {
        if (agentList.length === 0) return null;

        // If an agent is already selected in the main insights screen and it's not "All Agents", use it
        if (contextAgent && contextAgent.id) {
            const agent = agentList.find(a => a.id === contextAgent.id);
            return agent || agentList[0];
        }

        // Otherwise, select the first agent
        return agentList[0];
    });

    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [tempStartDate, setTempStartDate] = useState<string>('');
    const [tempEndDate, setTempEndDate] = useState<string>('');
    const [dateRangeAnchor, setDateRangeAnchor] = useState<HTMLElement | null>(null);
    const startDateInputRef = useRef<HTMLInputElement>(null);
    const endDateInputRef = useRef<HTMLInputElement>(null);

    const handleClose = () => {
        clearError();
        onClose();
    };

    const handleDateRangeClick = (event: React.MouseEvent<HTMLElement>) => {
        setDateRangeAnchor(event.currentTarget);
        setTempStartDate(startDate);
        setTempEndDate(endDate);
    };

    const handleDateRangeClose = () => {
        setDateRangeAnchor(null);
    };

    const handleClearAll = () => {
        setTempStartDate('');
        setTempEndDate('');
    };

    const handleApply = () => {
        setStartDate(tempStartDate);
        setEndDate(tempEndDate);
        clearError();
        handleDateRangeClose();
    };

    const getDateRangeLabel = () => {
        if (!startDate && !endDate) {
            return ALL_TIME_LABEL;
        }

        const formatDate = (dateStr: string) => {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        };

        const start = startDate ? formatDate(startDate) : 'Start';
        const end = endDate ? formatDate(endDate) : 'End';

        return `${start} - ${end}`;
    };

    const handleExport = async () => {
        if (!selectedAgent?.id) {
            return;
        }

        // Use epoch date if start date is empty (means "all time")
        const fromDate = startDate ? new Date(startDate) : new Date('1970-01-01');
        fromDate.setHours(0, 0, 0, 0);

        // Use today if end date is empty
        const toDate = endDate ? new Date(endDate) : new Date();
        toDate.setHours(23, 59, 59, 999);

        const payload = {
            agent_id: selectedAgent.id,
            from: fromDate.toISOString().split('T')[0] + 'T00:00:00Z',
            to: toDate.toISOString().split('T')[0] + 'T23:59:59Z',
        };

        await exportChats(payload);

        if (!error) {
            handleClose();
        }
    };

    return (
        <>
            <BootstrapDialog open onClose={handleClose} width="56rem" PaperProps={{ style: { borderRadius: '1.2rem' } }}>
                <StyledDialogTitle>
                    <Typography variant="h2">Export conversations</Typography>
                    <IconButtonStyled ariaLabel="close" onClick={handleClose}>
                        <CloseIcon />
                    </IconButtonStyled>
                </StyledDialogTitle>

                <StyledDialogContent>
                    <Subtitle variant="body1">Select an Agent and date range to export your conversations</Subtitle>

                    <FormRow>
                        <Label>Agent</Label>
                        <StyledAdornmentSelect
                            id="export-agent-select"
                            label=""
                            options={agentList}
                            getOptionLabel={option => option?.preview_name || ''}
                            value={selectedAgent}
                            onChange={(_, value) => {
                                setSelectedAgent(value);
                                clearError();
                            }}
                            className="options-select"
                            optionSx={{ fontWeight: 400 }}
                            paperSx={{ borderRadius: '0.8rem' }}
                            disableClearable={true}
                        />
                    </FormRow>

                    <FormRow>
                        <Label>Date range</Label>
                        <DateRangeButton
                            onClick={handleDateRangeClick}
                            hasValue={!!(startDate || endDate)}
                            startIcon={<MaskedIcon src={CalendarIcon} size="24px" />}
                            endIcon={<MaskedIcon src={ArrowDownIcon} size="24px" />}>
                            {getDateRangeLabel()}
                        </DateRangeButton>
                    </FormRow>

                    {error && (
                        <AlertContainer>
                            <Alert severity="error">{error}</Alert>
                        </AlertContainer>
                    )}
                </StyledDialogContent>

                <StyledDialogActions>
                    <ExportButton
                        onClick={handleExport}
                        disabled={isExporting || !selectedAgent}
                        variant="contained"
                        color="secondary"
                        isLoading={isExporting}
                        loadingText="Exporting...">
                        Export
                    </ExportButton>
                </StyledDialogActions>
            </BootstrapDialog>

            <Popover
                open={!!dateRangeAnchor}
                anchorEl={dateRangeAnchor}
                onClose={handleDateRangeClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            marginTop: 1,
                            borderRadius: '8px',
                            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                        },
                    },
                }}>
                <DateRangePopover>
                    <DateInputRow>
                        <DateLabel>Start</DateLabel>
                        <TextField
                            type="date"
                            value={tempStartDate}
                            onChange={e => setTempStartDate(e.target.value)}
                            onClick={() => startDateInputRef.current?.showPicker?.()}
                            inputRef={startDateInputRef}
                            size="small"
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    cursor: 'pointer',
                                },
                                '& .MuiOutlinedInput-input': {
                                    cursor: 'pointer',
                                },
                                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: `${theme.colors.black[300]} !important`,
                                },
                                '& .MuiOutlinedInput-root:hover fieldset': {
                                    borderColor: `${theme.colors.black[300]} !important`,
                                },
                            }}
                        />
                    </DateInputRow>
                    <DateInputRow>
                        <DateLabel>End</DateLabel>
                        <TextField
                            type="date"
                            value={tempEndDate}
                            onChange={e => setTempEndDate(e.target.value)}
                            onClick={() => endDateInputRef.current?.showPicker?.()}
                            inputRef={endDateInputRef}
                            size="small"
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    cursor: 'pointer',
                                },
                                '& .MuiOutlinedInput-input': {
                                    cursor: 'pointer',
                                },
                                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: `${theme.colors.black[300]} !important`,
                                },
                                '& .MuiOutlinedInput-root:hover fieldset': {
                                    borderColor: `${theme.colors.black[300]} !important`,
                                },
                            }}
                        />
                    </DateInputRow>
                    <DateRangeActions>
                        <ActionButton variant="text" color="secondary" onClick={handleClearAll}>
                            Clear all
                        </ActionButton>
                        <ActionButton variant="contained" color="primary" onClick={handleApply}>
                            Apply
                        </ActionButton>
                    </DateRangeActions>
                </DateRangePopover>
            </Popover>
        </>
    );
};
